import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import {
  getSpecializationOptions,
  getTeamCapacity,
  updateUwCapacity,
} from './api';
import { GridCell } from './shared-cells';
import { containsFilterColumnProps, managerTabGridProps } from './shared-grid';
import type {
  SpecializationOption,
  TeamCapacityItem,
  UpdateUwCapacityPayload,
} from './types';
import { useGridView } from './view-state';

// Authority limit names are stored as raw numbers ("1000000", "250000"). Format to "$1M" / "$250K".
function fmtAuthorityLimit(name: string): string {
  const n = Number(name);
  if (!isNaN(n)) {
    if (n >= 1_000_000) return `$${n / 1_000_000}M`;
    if (n >= 1_000) return `$${n / 1_000}K`;
  }
  return name;
}

// Specialization names are stored as SCREAMING_SNAKE_CASE. Convert to Title Case for display.
function fmtSpecName(name: string): string {
  return name
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Chips shown in the SPECIALIZATIONS cell: the UW's specializations, then the authority limit.
function getSpecializationChips(row: TeamCapacityItem) {
  return [
    ...row.specializations.map(s => ({
      key: s.id,
      label: s.displayText ?? fmtSpecName(s.name),
      authority: false,
    })),
    ...(row.authorityLimit
      ? [
          {
            key: row.authorityLimit.id,
            label: fmtAuthorityLimit(row.authorityLimit.name),
            authority: true,
          },
        ]
      : []),
  ];
}

// ─── Progress bar cell ────────────────────────────────────────────────────────

function PlateProgressCell({ plate, cap }: { plate: number; cap: number }) {
  const pct = cap > 0 ? Math.min((plate / cap) * 100, 100) : 0;
  return (
    <Box
      sx={{
        width: '100%',
        pr: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={pct}
        color="warning"
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 0.5,
          opacity: cap === 0 ? 0.25 : 1,
        }}
      />
      <Typography
        variant="caption"
        color={cap === 0 ? 'text.disabled' : 'text.secondary'}
      >
        {plate}/{cap}
      </Typography>
    </Box>
  );
}

// ─── Status chip ──────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: 'active' | 'paused' }) {
  return status === 'active' ? (
    <Chip
      icon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
      label="Active"
      color="success"
      size="small"
      variant="filled"
    />
  ) : (
    <Chip
      icon={<PauseIcon sx={{ fontSize: 14 }} />}
      label="Paused"
      color="error"
      size="small"
      variant="filled"
    />
  );
}

// ─── Toggle chip group ────────────────────────────────────────────────────────

function ToggleChipGroup({
  options,
  selectedIds,
  onChange,
}: {
  options: SpecializationOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id],
    );
  };
  return (
    <Box display="flex" flexWrap="wrap" gap={0.75}>
      {options.map(opt => {
        const selected = selectedIds.includes(opt.id);
        return (
          <Chip
            key={opt.id}
            label={opt.displayText ?? fmtSpecName(opt.name)}
            size="small"
            onClick={() => toggle(opt.id)}
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer', fontWeight: selected ? 600 : 400 }}
          />
        );
      })}
    </Box>
  );
}

// ─── Edit Drawer ──────────────────────────────────────────────────────────────

interface EditDrawerProps {
  uw: TeamCapacityItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
}

function EditDrawer({ uw, open, onClose, onSaved }: EditDrawerProps) {
  const queryClient = useQueryClient();

  const [dailyPointCap, setDailyPointCap] = useState('');
  const [concurrentCaseCeiling, setConcurrentCaseCeiling] = useState('');
  const [status, setStatus] = useState<'active' | 'paused'>('active');
  const [selectedLifecycleIds, setSelectedLifecycleIds] = useState<number[]>(
    [],
  );
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [selectedAuthorityLimitId, setSelectedAuthorityLimitId] = useState<
    number | ''
  >('');

  const [baselineCap, setBaselineCap] = useState('');
  const [baselineConcurrentCaseCeiling, setBaselineConcurrentCaseCeiling] =
    useState('');
  const [baselineStatus, setBaselineStatus] = useState<'active' | 'paused'>(
    'active',
  );
  const [baselineLifecycleIds, setBaselineLifecycleIds] = useState<number[]>(
    [],
  );
  const [baselineProductIds, setBaselineProductIds] = useState<number[]>([]);
  const [baselineAuthorityLimitId, setBaselineAuthorityLimitId] = useState<
    number | ''
  >('');

  const [errorMsg, setErrorMsg] = useState('');

  const {
    data: specOptions,
    isLoading: loadingOptions,
    isError: optionsFailed,
  } = useQuery({
    queryKey: ['manager-tab-specialization-options'],
    queryFn: getSpecializationOptions,
    enabled: open,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (uw) {
      const cap = String(uw.dailyPointCap);
      const ceiling = String(uw.concurrentCaseCeiling);
      const lifecycleIds = uw.specializations
        .filter(s => s.type === 'APPLICATION_LIFECYCLE')
        .map(s => s.id);
      const productIds = uw.specializations
        .filter(s => s.type === 'PRODUCT')
        .map(s => s.id);
      const authorityId = uw.authorityLimit?.id ?? '';

      setDailyPointCap(cap);
      setConcurrentCaseCeiling(ceiling);
      setStatus(uw.status);
      setSelectedLifecycleIds(lifecycleIds);
      setSelectedProductIds(productIds);
      setSelectedAuthorityLimitId(authorityId);

      setBaselineCap(cap);
      setBaselineConcurrentCaseCeiling(ceiling);
      setBaselineStatus(uw.status);
      setBaselineLifecycleIds(lifecycleIds);
      setBaselineProductIds(productIds);
      setBaselineAuthorityLimitId(authorityId);

      setErrorMsg('');
    }
  }, [uw]);

  const sortedEquals = (a: number[], b: number[]) =>
    JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

  const isDirty =
    dailyPointCap !== baselineCap ||
    concurrentCaseCeiling !== baselineConcurrentCaseCeiling ||
    status !== baselineStatus ||
    !sortedEquals(selectedLifecycleIds, baselineLifecycleIds) ||
    !sortedEquals(selectedProductIds, baselineProductIds) ||
    selectedAuthorityLimitId !== baselineAuthorityLimitId;

  const isValidCapacity = (value: string, baseline: string) => {
    if (value === baseline) {
      return true;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 && parsed <= 1000;
  };
  const capacitiesValid =
    isValidCapacity(dailyPointCap, baselineCap) &&
    isValidCapacity(concurrentCaseCeiling, baselineConcurrentCaseCeiling);

  const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: (payload: UpdateUwCapacityPayload) =>
      updateUwCapacity(uw!.userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['manager-tab-team-capacity'],
      });
      onSaved(`Changes saved for ${uw?.name ?? uw?.email ?? 'underwriter'}.`);
      onClose();
    },
    onError: (e: Error) => {
      setErrorMsg(e.message ?? 'Save failed.');
    },
  });

  const handleSave = () => {
    setErrorMsg('');
    const payload: UpdateUwCapacityPayload = {};

    if (!capacitiesValid) {
      setErrorMsg('Capacity values must be whole numbers between 1 and 1000.');
      return;
    }
    if (dailyPointCap !== baselineCap) {
      payload.dailyPointCap = Number(dailyPointCap);
    }
    if (concurrentCaseCeiling !== baselineConcurrentCaseCeiling) {
      payload.concurrentCaseCeiling = Number(concurrentCaseCeiling);
    }
    if (status !== baselineStatus) {
      payload.status = status;
    }
    if (!sortedEquals(selectedLifecycleIds, baselineLifecycleIds)) {
      payload.lifecycleSpecIds = selectedLifecycleIds;
    }
    if (!sortedEquals(selectedProductIds, baselineProductIds)) {
      payload.productSpecIds = selectedProductIds;
    }
    if (selectedAuthorityLimitId !== baselineAuthorityLimitId) {
      payload.authorityLimitId =
        selectedAuthorityLimitId === '' ? null : selectedAuthorityLimitId;
    }

    mutate(payload);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 400,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
        role="presentation"
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {uw?.name ?? uw?.email ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Changes take effect immediately
          </Typography>
        </Box>

        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

        <TextField
          label="Daily cap (pts)"
          type="number"
          value={dailyPointCap}
          onChange={e => setDailyPointCap(e.target.value)}
          inputProps={{ min: 0, max: 1000, step: 1 }}
          error={!isValidCapacity(dailyPointCap, baselineCap)}
          fullWidth
        />

        <TextField
          label="Concurrent case ceiling"
          type="number"
          value={concurrentCaseCeiling}
          onChange={e => setConcurrentCaseCeiling(e.target.value)}
          inputProps={{ min: 0, max: 1000, step: 1 }}
          error={
            !isValidCapacity(
              concurrentCaseCeiling,
              baselineConcurrentCaseCeiling,
            )
          }
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={e => setStatus(e.target.value as 'active' | 'paused')}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
          </Select>
        </FormControl>

        <Divider />

        {loadingOptions ? (
          <Box display="flex" alignItems="center" gap={1} py={1}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading options…
            </Typography>
          </Box>
        ) : optionsFailed ? (
          <Alert severity="error">Unable to load specialization options.</Alert>
        ) : (
          <>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
              >
                Lifecycle specializations
              </Typography>
              <ToggleChipGroup
                options={specOptions?.lifecycleOptions ?? []}
                selectedIds={selectedLifecycleIds}
                onChange={setSelectedLifecycleIds}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
              >
                Product specializations
              </Typography>
              <ToggleChipGroup
                options={specOptions?.productOptions ?? []}
                selectedIds={selectedProductIds}
                onChange={setSelectedProductIds}
              />
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel shrink>Authority limit</InputLabel>
              <Select
                value={selectedAuthorityLimitId}
                label="Authority limit"
                notched
                onChange={e =>
                  setSelectedAuthorityLimitId(e.target.value as number | '')
                }
                displayEmpty
              >
                <MenuItem value="">
                  <em style={{ opacity: 0.5 }}>— none —</em>
                </MenuItem>
                {(specOptions?.authorityLimitOptions ?? []).map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {fmtAuthorityLimit(opt.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Divider />

        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !isDirty || !capacitiesValid}
            fullWidth
          >
            {isSaving ? <CircularProgress size={18} /> : 'Save changes'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ─── Team Capacity tab ────────────────────────────────────────────────────────

export function TeamCapacityTab() {
  const [editTarget, setEditTarget] = useState<TeamCapacityItem | null>(null);
  // The search box, filter and sort are remembered while the app is open (see useGridView).
  const view = useGridView('team-capacity');
  const [search, setSearch] = useState(view.restored.search ?? '');
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['manager-tab-team-capacity'],
    queryFn: getTeamCapacity,
    refetchOnWindowFocus: false,
  });

  const columns = useMemo<GridColDef<TeamCapacityItem>[]>(
    () => [
      {
        field: 'name',
        headerName: 'UNDERWRITER',
        flex: 0.7,
        minWidth: 200,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <GridCell>
            <Tooltip title={row.name ?? row.email}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {row.name ?? row.email}
              </Typography>
            </Tooltip>
          </GridCell>
        ),
      },
      {
        field: 'dailyPointCap',
        headerName: 'DAILY CAP',
        width: 90,
        type: 'number',
        ...containsFilterColumnProps(),
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ value }) => (
          <GridCell>
            <Typography variant="body2">{value} pts</Typography>
          </GridCell>
        ),
      },
      {
        field: 'sodPlate',
        headerName: 'START-OF-DAY PLATE',
        width: 190,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <PlateProgressCell plate={row.sodPlate} cap={row.dailyPointCap} />
        ),
      },
      {
        field: 'currentPlate',
        headerName: 'CURRENT PLATE',
        width: 160,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <PlateProgressCell plate={row.currentPlate} cap={row.dailyPointCap} />
        ),
      },
      {
        field: 'openCaseCount',
        headerName: 'OPEN CASES',
        width: 120,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <PlateProgressCell
            plate={row.openCaseCount}
            cap={row.concurrentCaseCeiling}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'STATUS',
        width: 110,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ value }) => (
          <GridCell>
            <StatusChip status={value as 'active' | 'paused'} />
          </GridCell>
        ),
      },
      {
        field: 'specializations',
        headerName: 'SPECIALIZATIONS',
        flex: 2,
        minWidth: 340,
        // The chip labels as plain text (RP-8616). The grid sorts and filters on this value: sorting is
        // alphabetical, so UWs whose first specialization is the same end up together, and "contains"
        // matches the labels shown. The cell itself renders from `row`.
        valueGetter: (_value, row) =>
          getSpecializationChips(row)
            .map(c => c.label)
            .join(', '),
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => {
          const allChips = getSpecializationChips(row);
          const MAX_VISIBLE = 6;
          const visible = allChips.slice(0, MAX_VISIBLE);
          const overflow = allChips.length - MAX_VISIBLE;
          if (allChips.length === 0) {
            return (
              <GridCell>
                <Typography variant="caption" color="text.disabled">
                  None
                </Typography>
              </GridCell>
            );
          }
          return (
            <Box
              display="flex"
              flexWrap="nowrap"
              gap={0.5}
              alignItems="center"
              sx={{ height: '100%', width: '100%', overflow: 'hidden' }}
            >
              {visible.map(c => (
                <Chip
                  key={c.key}
                  label={c.label}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, fontWeight: 500, flexShrink: 0 }}
                />
              ))}
              {overflow > 0 && (
                <Tooltip
                  title={allChips
                    .slice(MAX_VISIBLE)
                    .map(c => c.label)
                    .join(', ')}
                  placement="top"
                >
                  <Chip
                    label={`+${overflow}`}
                    size="small"
                    sx={{
                      fontSize: 11,
                      flexShrink: 0,
                      bgcolor: 'grey.100',
                      cursor: 'default',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: '_actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <GridCell>
            <Button size="small" onClick={() => setEditTarget(row)}>
              Edit
            </Button>
          </GridCell>
        ),
      },
    ],
    [setEditTarget],
  );

  const underwriters = data?.underwriters ?? [];

  const filteredRows = useMemo(() => {
    if (!search.trim()) return underwriters;
    const q = search.toLowerCase();
    return underwriters.filter(
      u =>
        (u.name ?? '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [underwriters, search]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load team capacity: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <>
      {/* Search */}
      <TextField
        placeholder="Search underwriter..."
        size="small"
        value={search}
        onChange={e => {
          setSearch(e.target.value);
          view.saveSearch(e.target.value);
        }}
        sx={{ mb: 1.5, width: 320 }}
      />

      {/* Info banner */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ color: 'text.secondary', fontSize: 13 }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">
          Read-only. Click <strong>Edit</strong> to update daily cap, status, or
          specializations. Changes take effect immediately. &nbsp;
          <Tooltip title="Cases currently in an inclusion lifecycle stage">
            <Box
              component="span"
              display="inline-flex"
              alignItems="center"
              gap={0.4}
              sx={{ cursor: 'default' }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  display: 'inline-block',
                }}
              />
              open
            </Box>
          </Tooltip>
        </Typography>
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={r => r.userId}
        autoHeight
        disableRowSelectionOnClick
        {...managerTabGridProps}
        pageSizeOptions={[25, 50]}
        initialState={{
          ...view.initialState,
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        onFilterModelChange={view.saveFilter}
        onSortModelChange={view.saveSort}
        rowHeight={56}
        sx={{ bgcolor: 'background.paper' }}
      />

      <EditDrawer
        uw={editTarget}
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        onSaved={setSnackbarMsg}
      />

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={4000}
        onClose={() => setSnackbarMsg('')}
        message={snackbarMsg}
      />
    </>
  );
}
